import type { ReactionCamEligibility, ReactionRecorderProfile } from './types.ts';

const APPLE_PLATFORM = /iPad|iPhone|iPod|Macintosh/i;

const PROFILES: readonly ReactionRecorderProfile[] = [
  {
    mimeType: 'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    extension: 'mp4',
    videoBitsPerSecond: 1_200_000,
    audioBitsPerSecond: 96_000,
  },
  {
    mimeType: 'video/mp4',
    extension: 'mp4',
    videoBitsPerSecond: 1_200_000,
    audioBitsPerSecond: 96_000,
  },
  {
    mimeType: 'video/webm;codecs=vp8,opus',
    extension: 'webm',
    videoBitsPerSecond: 1_200_000,
    audioBitsPerSecond: 96_000,
  },
  {
    mimeType: 'video/webm;codecs=vp9,opus',
    extension: 'webm',
    videoBitsPerSecond: 1_200_000,
    audioBitsPerSecond: 96_000,
  },
  {
    mimeType: 'video/webm',
    extension: 'webm',
    videoBitsPerSecond: 1_200_000,
    audioBitsPerSecond: 96_000,
  },
];

export function detectReactionCamEligibility(options: {
  readonly platformAvailable: boolean;
  readonly eligibleAccount?: boolean;
  readonly secureContext?: boolean;
}): ReactionCamEligibility {
  if (!options.platformAvailable)
    return {
      state: 'platform_unavailable',
      reason: 'Reaction Cam is not enabled for this runtime.',
    };
  if (options.eligibleAccount === false)
    return {
      state: 'ineligible_account',
      reason: 'Reaction Cam is not available for this account.',
    };
  if (!(options.secureContext ?? globalThis.isSecureContext))
    return {
      state: 'insecure_context',
      reason: 'Reaction Cam requires a secure HTTPS connection.',
    };
  if (
    typeof navigator === 'undefined' ||
    !navigator.mediaDevices?.getUserMedia ||
    typeof MediaRecorder === 'undefined'
  )
    return {
      state: 'unsupported_browser',
      reason: 'This browser cannot record Reaction Cam video.',
    };
  if (!preferredReactionRecorderProfile())
    return {
      state: 'unsupported_browser',
      reason: 'This browser does not expose a playable recording format.',
    };
  return { state: 'available', reason: 'Reaction Cam is available.' };
}

export function supportedReactionRecorderProfiles(
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): readonly ReactionRecorderProfile[] {
  if (typeof MediaRecorder === 'undefined') return [];
  const supported = PROFILES.filter((profile) => MediaRecorder.isTypeSupported(profile.mimeType));
  const apple = APPLE_PLATFORM.test(userAgent);
  return [...supported].sort((left, right) => {
    const leftApple = left.extension === 'mp4';
    const rightApple = right.extension === 'mp4';
    if (leftApple === rightApple) return 0;
    return apple ? (leftApple ? -1 : 1) : leftApple ? 1 : -1;
  });
}

export function preferredReactionRecorderProfile(): ReactionRecorderProfile | null {
  return supportedReactionRecorderProfiles()[0] ?? null;
}

export interface ReactionPermissionQuery {
  query(descriptor: PermissionDescriptor): Promise<Pick<PermissionStatus, 'state'>>;
}

export async function reactionPermissionAlreadyGranted(
  name: 'camera' | 'microphone',
  permissions: ReactionPermissionQuery | null = typeof navigator === 'undefined'
    ? null
    : navigator.permissions,
): Promise<boolean> {
  if (!permissions) return false;
  try {
    const status = await permissions.query({ name } as PermissionDescriptor);
    return status.state === 'granted';
  } catch {
    // Camera and microphone permission queries are not implemented consistently
    // across supported browsers. Unsupported queries must leave the explicit
    // Enable Camera action in place rather than triggering a permission prompt.
    return false;
  }
}
